/*
 * Mantine theme — the bridge between tokens.css and every Mantine component
 * (2026-07-21 polish pass). Before this, the provider ran on Mantine DEFAULTS:
 * stock blue actions, stock radii, stock focus rings — visually disconnected
 * from the token system the custom components consume. One theme object,
 * created once at module scope (zero render cost), unifies the two worlds.
 *
 * Design language: precision-instrument minimalism. One indigo accent
 * (--color-accent #5457e6), a 4px grid, 6px default radius, dense 14px body,
 * and monospace for identifiers/metrics. No webfonts — the system stack keeps
 * first paint instant; character comes from spacing, hierarchy, and the
 * trajectory brand mark, not font downloads.
 */
import { createTheme, rem } from '@mantine/core';

/** The indigo accent as a full Mantine 10-shade scale (index 6 = --color-accent). */
const BRAND: readonly [string, string, string, string, string, string, string, string, string, string] = [
  '#eef0fe', // 0 — subtle tinted surfaces (selected nav, chips)
  '#dfe2fd',
  '#c5c9fa',
  '#a7adf6',
  '#888ff0',
  '#6b71ea',
  '#5457e6', // 6 — the action colour (AA on white)
  '#4548c9', // 7 — hover
  '#393cb0', // 8 — active
  '#2e3092',
];

export const theme = createTheme({
  primaryColor: 'brand',
  primaryShade: 6,
  colors: { brand: BRAND },

  fontFamily:
    '"Inter var", Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  fontFamilyMonospace:
    '"JetBrains Mono", ui-monospace, "SF Mono", "Cascadia Code", "Roboto Mono", monospace',

  // Dense operator scale — matches tokens.css (--text-*).
  fontSizes: {
    xs: rem(12),
    sm: rem(13),
    md: rem(14),
    lg: rem(16),
    xl: rem(20),
  },

  headings: {
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(24), lineHeight: '1.2' },
      h2: { fontSize: rem(20), lineHeight: '1.25' },
      h3: { fontSize: rem(16), lineHeight: '1.3' },
      h4: { fontSize: rem(14), lineHeight: '1.4' },
    },
  },

  defaultRadius: 'md',
  radius: {
    xs: rem(3),
    sm: rem(4),
    md: rem(6),
    lg: rem(10),
    xl: rem(14),
  },

  // Softer, larger-radius overlays; hairline cards.
  shadows: {
    xs: '0 1px 2px rgba(16, 24, 40, 0.05)',
    sm: '0 1px 2px rgba(16, 24, 40, 0.06)',
    md: '0 4px 16px rgba(16, 24, 40, 0.10)',
    lg: '0 12px 40px rgba(16, 24, 40, 0.16)',
    xl: '0 24px 64px rgba(16, 24, 40, 0.20)',
  },

  cursorType: 'pointer',

  components: {
    Button: {
      defaultProps: { radius: 'md' },
      styles: { label: { fontWeight: 500 } },
    },
    TextInput: { defaultProps: { radius: 'md' } },
    Select: { defaultProps: { radius: 'md' } },
    Paper: { defaultProps: { radius: 'lg' } },
    Modal: { defaultProps: { radius: 'lg', overlayProps: { blur: 4, backgroundOpacity: 0.4 } } },
    Menu: { defaultProps: { radius: 'md', shadow: 'md' } },
    Table: {
      styles: {
        th: {
          fontSize: rem(12),
          fontWeight: 600,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.04em',
          color: 'var(--color-text-muted)',
        },
      },
    },
    Badge: { defaultProps: { radius: 'sm' }, styles: { label: { fontWeight: 500 } } },
    Tooltip: { defaultProps: { radius: 'sm' } },
  },
});
